import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { salesAPI } from '../lib/api';
import { formatCurrency, formatDateTime, paymentMethodLabels, cn } from '../lib/utils';
import logger from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ShieldCheck, ShieldX, Loader2, Inbox, Clock, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

export default function AutorizacionesPage() {
  const { user, selectedBusiness } = useAuth();
  const isMasterAdmin = !!user?.is_master_admin;

  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [activeReq, setActiveReq] = useState(null);
  const [action, setAction] = useState(null); // 'approve' | 'reject'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const { data } = await salesAPI.getEditRequests(tab === 'all' ? undefined : tab);
      setRequests(data?.requests || []);
    } catch (err) {
      logger.error('Load edit requests failed', err);
      toast.error('No se pudieron cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, tab]);

  useEffect(() => { load(); }, [load]);

  const openAction = (req, kind) => {
    setActiveReq(req);
    setAction(kind);
    setNotes('');
  };

  const closeAction = () => {
    setActiveReq(null);
    setAction(null);
    setNotes('');
  };

  const submit = async () => {
    if (!activeReq || !action) return;
    setSubmitting(true);
    try {
      if (action === 'approve') {
        await salesAPI.approveEditRequest(activeReq.id, notes);
        toast.success('Solicitud aprobada. Comisiones recalculadas.');
      } else {
        await salesAPI.rejectEditRequest(activeReq.id, notes);
        toast.success('Solicitud rechazada. Monto revertido.');
      }
      closeAction();
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s) => {
    if (s === 'pending') return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pendiente</Badge>;
    if (s === 'approved') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Aprobada</Badge>;
    return <Badge className="bg-red-100 text-red-800 border-red-200">Rechazada</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="autorizaciones-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Autorizaciones
        </h1>
        <p className="text-slate-500 mt-1">
          Solicitudes de cambio de monto en ventas — {selectedBusiness?.name}
        </p>
      </div>

      <Card className="border-slate-200 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Inbox className="w-5 h-5" />
            Solicitudes de cambio de total
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending" data-testid="tab-pending">Pendientes</TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Aprobadas</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="tab-rejected">Rechazadas</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">Todas</TabsTrigger>
            </TabsList>

            <TabsContent value={tab}>
              {loading ? (
                <div className="flex items-center justify-center h-40 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
                </div>
              ) : requests.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-500" data-testid="empty-requests">
                  <Inbox className="w-10 h-10 mb-2 opacity-50" />
                  <p>Sin solicitudes {tab !== 'all' ? tab : ''}</p>
                </div>
              ) : (
                <div className="max-h-[70vh] overflow-y-auto">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Solicitada</TableHead>
                        <TableHead>Ticket</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Anterior</TableHead>
                        <TableHead className="text-right">Nuevo</TableHead>
                        <TableHead className="text-right">Δ</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Solicitante</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((r) => {
                        const positive = (r.delta || 0) >= 0;
                        return (
                          <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                            <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                              {formatDateTime(r.requested_at)}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {r.ticket_number || r.sale_id?.slice(0, 8)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.sale?.client_name || 'Sin cliente'}
                              {r.sale?.payment_method && (
                                <div className="text-[10px] text-slate-400 uppercase">
                                  {paymentMethodLabels[r.sale.payment_method] || r.sale.payment_method}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 line-through text-sm">
                              {formatCurrency(r.old_total)}
                            </TableCell>
                            <TableCell className="text-right font-bold text-sm">
                              {formatCurrency(r.new_total)}
                            </TableCell>
                            <TableCell className={cn(
                              'text-right text-sm font-semibold flex items-center justify-end gap-1',
                              positive ? 'text-emerald-600' : 'text-red-600'
                            )}>
                              {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                              {positive ? '+' : ''}{formatCurrency(r.delta)}
                            </TableCell>
                            <TableCell className="max-w-[220px]">
                              <p className="text-sm text-slate-700 line-clamp-2" title={r.reason}>
                                {r.reason}
                              </p>
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.requested_by_name}
                              <div className="text-[10px] text-slate-400 uppercase">{r.requested_by_role}</div>
                            </TableCell>
                            <TableCell>{statusBadge(r.status)}</TableCell>
                            <TableCell className="text-right">
                              {r.status === 'pending' ? (
                                isMasterAdmin ? (
                                  <div className="flex gap-1 justify-end">
                                    <Button
                                      size="sm"
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                                      onClick={() => openAction(r, 'approve')}
                                      data-testid={`approve-btn-${r.id}`}
                                    >
                                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Aprobar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-200 hover:bg-red-50 h-8"
                                      onClick={() => openAction(r, 'reject')}
                                      data-testid={`reject-btn-${r.id}`}
                                    >
                                      <ShieldX className="w-3.5 h-3.5 mr-1" /> Rechazar
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> Esperando master
                                  </span>
                                )
                              ) : (
                                <div className="text-xs text-slate-500">
                                  <div>{r.resolved_by_name || '—'}</div>
                                  <div className="text-[10px] text-slate-400">
                                    {r.resolved_at ? formatDateTime(r.resolved_at) : ''}
                                  </div>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={!!action} onOpenChange={(o) => !o && closeAction()}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' ? 'Aprobar cambio de monto' : 'Rechazar cambio de monto'}
            </DialogTitle>
            <DialogDescription>
              {action === 'approve'
                ? 'El nuevo monto quedará confirmado y las comisiones asociadas se recalcularán proporcionalmente.'
                : 'Se revertirá el monto de la venta al valor original y se ajustará la caja si aplica.'}
            </DialogDescription>
          </DialogHeader>

          {activeReq && (
            <div className="text-sm space-y-2 py-2">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ticket</span>
                  <span className="font-mono">{activeReq.ticket_number || activeReq.sale_id?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Anterior</span>
                  <span className="line-through text-slate-500">{formatCurrency(activeReq.old_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Nuevo</span>
                  <span className="font-bold">{formatCurrency(activeReq.new_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Motivo</span>
                  <span className="text-right max-w-[240px]">{activeReq.reason}</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Notas (opcional)</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="resize-none"
                  placeholder={action === 'approve' ? 'Autorizado por…' : 'Motivo del rechazo…'}
                  data-testid="resolution-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeAction} disabled={submitting}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className={cn(
                action === 'approve'
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              )}
              data-testid="confirm-resolution-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {action === 'approve' ? 'Aprobar' : 'Rechazar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
